/**
 * V2 Category — data access. Split out of category.js on 2026-07-25 (998
 * lines against the plan's ~400 budget), unchanged. category.js imports these
 * back; they were module-private before and stay off the public surface
 * except SUBTREE_CAP, which mount reads.
 */
import * as gateway from '../../core/gateway.js';
import { cached } from '../../core/query.js';
import { drainRows } from '../../core/drain.js';

/**
 * The category's yoinks INCLUDING every descendant category, via the
 * get_category_yoinks RPC (20260224 migration — recursive CTE server-
 * side). The old eq.category_id filter silently dropped all subtree
 * yoinks: a parent category page showed only directly-tagged rows
 * (M8.5 audit). Capped at 2000 like V1; these rows also feed the
 * header stats, chip counts, progress, rank, hero collage, and nearby
 * card — V1 computed all of them from this same load.
 */
export const SUBTREE_CAP = 2000;

export async function fetchSubtreeYoinks(categoryId) {
  return cached(`category-subtree:${categoryId}`, async () => {
    const { data, error } = await gateway.rawRpc('get_category_yoinks', {
      params: { p_category_id: categoryId, p_limit: SUBTREE_CAP },
    });
    if (error) throw error;
    return Array.isArray(data) ? data : [];
  });
}

/**
 * The category's "About" article (20260813). Its own one-row read rather
 * than a column on the shared catalog fetch: articles are a few KB each and
 * the catalog pulls 2,000+ rows for every visitor. An error — including the
 * 400 PostgREST returns while the hand-applied migration hasn't been run —
 * resolves to null and the About section simply stays away.
 */
export async function fetchCategoryArticle(categoryId) {
  return cached(`category-article:${categoryId}`, async () => {
    const { data, error } = await gateway.rawQuery(gateway.TABLES.CATEGORIES, {
      select: 'article',
      filters: { id: `eq.${categoryId}` },
      limit: 1,
    });
    if (error) return null;
    return data?.[0]?.article ?? null;
  });
}

export async function fetchMyYoinkedCategoryIds(userId) {
  return cached(`my-yoinked-cats:${userId}`, async () => {
    // Drained past the old silent 1000-row cap (assessment 1.5): the Set
    // checkmarks were quietly wrong for prolific explorers
    const data = await drainRows(gateway.TABLES.YOINKS, {
      select: 'id,category_id',
      filters: { user_id: `eq.${userId}` },
    });
    return new Set(data.map((y) => y.category_id).filter(Boolean));
  });
}
